import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import config from "console_config";

import { fetchFromNewStatusApi } from "./thunks/fetchFromNewStatusApi.thunk";
import { Incident, ScheduledMaintenance, StatusPageStatus } from "./types";

const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes

export const getSystemStatus = createAsyncThunk(
  "system/status",
  async (_arg, { getState }) => {
    const state = getState() as { systemStatus: SystemStatusState };
    const { lastFetchedAt } = state.systemStatus;

    // Stale-while-revalidate: skip if data is fresh
    if (lastFetchedAt && Date.now() - lastFetchedAt < CACHE_TTL_MS) {
      return undefined;
    }

    if (!config.URL_STATUS_API) return undefined;

    return await fetchFromNewStatusApi();
  }
);

export type SystemStatusState = {
  loading: boolean | "idle";
  errors?: string;
  status?: StatusPageStatus;
  incidents: Incident[];
  maintenances: ScheduledMaintenance[];
  lastFetchedAt?: number;
};

const initialState: SystemStatusState = {
  loading: "idle",
  incidents: [],
  maintenances: []
};

const systemStatus = createSlice({
  name: "systemStatus",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getSystemStatus.pending, state => {
        // Only show loading spinner on first fetch
        if (state.loading === "idle") {
          state.loading = true;
        }
      })
      .addCase(getSystemStatus.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.error.message;
      })
      .addCase(getSystemStatus.fulfilled, (state, action) => {
        const payload = action.payload;

        state.loading = false;

        // Skip if stale-while-revalidate returned early
        if (!payload) {
          return;
        }

        if (!payload.status) {
          return;
        }

        state.status = payload.status;
        state.incidents = payload.incidents;
        state.maintenances = payload.scheduled_maintenances;
        state.lastFetchedAt = Date.now();
      });
  }
});

export default systemStatus.reducer;
